import { useEffect, useRef, useState } from 'react';
import { Button, Divider, Drawer, Input, Select, Tooltip } from 'antd';
import {
  AlignLeftOutlined,
  BoldOutlined,
  FontColorsOutlined,
  HighlightOutlined,
  ItalicOutlined,
  LinkOutlined,
  MailOutlined,
  OrderedListOutlined,
  PaperClipOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { getBulkMailDetails, sendBulkMail } from '../../services/dropdownApi';
import AppTypography from '../typography/Typography';

const TOOLBAR_BUTTONS = [
  { icon: <BoldOutlined />, title: 'Bold' },
  { icon: <ItalicOutlined />, title: 'Italic' },
  { icon: <UnderlineOutlined />, title: 'Underline' },
  { icon: <LinkOutlined />, title: 'Link' },
  { icon: <OrderedListOutlined />, title: 'Ordered list' },
  { icon: <UnorderedListOutlined />, title: 'Bullet list' },
  { icon: <FontColorsOutlined />, title: 'Font color' },
  { icon: <HighlightOutlined />, title: 'Highlight' },
  { icon: <AlignLeftOutlined />, title: 'Align' },
  { icon: <PaperClipOutlined />, title: 'Attachment' },
];

const TEXT_STYLE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
];

export default function MailPopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedIds = [],
    selectedRecords = [],
    recipients = [],
    subject: initialSubject = '',
    onSend,
  } = location.state ?? {};

  const [to, setTo] = useState(recipients.join(', '));
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(initialSubject);
  const editorRef = useRef(null);

  const jobId = selectedIds[0]
    ?? selectedRecords[0]?._id
    ?? selectedRecords[0]?.id;

  useEffect(() => {
    if (!jobId) return;
    getBulkMailDetails(jobId).then((data) => {
      if (data.subject) setSubject(data.subject);
      if (data.toMail?.length) setTo(Array.isArray(data.toMail) ? data.toMail.join(', ') : String(data.toMail));
      if (data.ccMail?.length) { setCc(Array.isArray(data.ccMail) ? data.ccMail.join(', ') : String(data.ccMail)); setShowCc(true); }
      if (data.bccMail?.length) { setBcc(Array.isArray(data.bccMail) ? data.bccMail.join(', ') : String(data.bccMail)); setShowBcc(true); }
      if (data.contents && editorRef.current) {
        editorRef.current.innerHTML = data.contents;
      }
    }).catch(() => {});
  }, [jobId]);

  const canSend = to.trim() && subject.trim();

  function handleClose() {
    navigate(-1);
  }

  function parseEmails(value) {
    return value.split(',').map((e) => e.trim()).filter(Boolean);
  }

  function handleSend() {
    if (!canSend) return;
    const contents = editorRef.current?.innerHTML ?? '';

    sendBulkMail({
      jobId,
      toMail: parseEmails(to),
      ccMail: parseEmails(cc),
      bccMail: parseEmails(bcc),
      subject,
      contents,
    }).then(() => {
      if (typeof onSend === 'function') onSend({ to, cc, bcc, subject, message: contents, selectedIds });
      navigate(-1);
    }).catch(() => {});
  }

  const footer = (
    <div className="mail-popup__footer">
      <Button onClick={handleClose}>Cancel</Button>
      <Button disabled={!canSend} onClick={handleSend}>Send</Button>
    </div>
  );

  return (
    <Drawer
      open
      placement="right"
      onClose={handleClose}
      closable={false}
      rootClassName="mail-popup"
      width={560}
      footer={footer}
      maskClosable={false}
    >
      {/* Header */}
      <div className="mail-popup__header">
        <div className="mail-popup__header-brand">
          <MailOutlined className="mail-popup__header-icon" />
          <AppTypography tag="span" variant="h2" color="link">Mail</AppTypography>
        </div>
        <div className="mail-popup__header-controls">
          <Select
            placeholder="Module"
            className="mail-popup__module-select"
            options={[]}
            allowClear
          />
          <Select
            placeholder="Template"
            className="mail-popup__template-select"
            options={[]}
            allowClear
          />
        </div>
      </div>

      {/* Body */}
      <div className="mail-popup__body">

        {/* To */}
        <div className="mail-popup__field-row">
          <AppTypography variant="body" color="muted" className="mail-popup__field-label">
            To
          </AppTypography>
          <Input
            variant="borderless"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mail-popup__field-input"
          />
          <div className="mail-popup__cc-bcc-actions">
            {!showCc && (
              <span className="mail-popup__cc-bcc-link" onClick={() => setShowCc(true)}>
                Cc
              </span>
            )}
            {!showBcc && (
              <span className="mail-popup__cc-bcc-link" onClick={() => setShowBcc(true)}>
                Bcc
              </span>
            )}
          </div>
        </div>
        <Divider />

        {showCc && (
          <>
            <div className="mail-popup__field-row">
              <AppTypography variant="body" color="muted" className="mail-popup__field-label">
                Cc
              </AppTypography>
              <Input
                variant="borderless"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                className="mail-popup__field-input"
              />
            </div>
            <Divider />
          </>
        )}

        {showBcc && (
          <>
            <div className="mail-popup__field-row">
              <AppTypography variant="body" color="muted" className="mail-popup__field-label">
                Bcc
              </AppTypography>
              <Input
                variant="borderless"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                className="mail-popup__field-input"
              />
            </div>
            <Divider />
          </>
        )}

        {/* Subject */}
        <div className="mail-popup__field-row">
          <AppTypography variant="body" color="muted" className="mail-popup__field-label">
            Subject
          </AppTypography>
          <Input
            variant="borderless"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter subject"
            className="mail-popup__field-input"
          />
        </div>
        <Divider />

        {/* Message */}
        <div className="mail-popup__message-section">
          <AppTypography variant="label" color="muted" className="mail-popup__message-label">
            Message
          </AppTypography>

          <div className="mail-popup__editor-box">
            <div className="mail-popup__toolbar">
              <Select
                size="small"
                defaultValue="normal"
                variant="borderless"
                className="mail-popup__toolbar-style-select"
                options={TEXT_STYLE_OPTIONS}
                popupMatchSelectWidth={false}
              />
              {TOOLBAR_BUTTONS.map(({ icon, title }) => (
                <Tooltip key={title} title={title} mouseEnterDelay={0.5}>
                  <Button
                    type="text"
                    size="small"
                    icon={icon}
                    className="mail-popup__toolbar-btn"
                  />
                </Tooltip>
              ))}
            </div>

            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              className="mail-popup__textarea"
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
}
